import { useJobToast } from '@/components/async-job-toast';
import { ResizableContainer } from '@/comps/buttons/ResizableContainer';
import { WithLoadingContainer } from '@/comps/loading-spinner';
import { Button } from '@/comps/primitives/button';
import { Input } from '@/comps/primitives/input';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/comps/primitives/sheet';
import { Textarea } from '@/comps/primitives/textarea';
import { QUERY_KEY_GET_CATEGORY, QUERY_KEY_INTERACTION_AGGREGATES, QUERY_KEY_INTERACTION_DETAILS, QUERY_KEY_USER_INTENTS_PAGE__ACTIONS, QUERY_KEY_USER_INTENTS_PAGE__TOTALS, QUERY_KEY_USER_USER_INTELLIGENCE_VERTICAL_TABLE__ACTIONS, QUERY_KEY_USER_USER_INTELLIGENCE_VERTICAL_TABLE__TOPICS } from '@/constants/query-keys';
import { useProjectsContext } from '@/providers/projects-provider';
import { humanizeString } from '@/utils/string.utils';
import { DoubleArrowRightIcon } from '@radix-ui/react-icons';
import { Slot } from '@radix-ui/react-slot';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isHttpError } from '@/api/http-error';
import { Loader2Icon, Pencil } from 'lucide-react';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { getCategory, useUpdateCategory } from '@/api/generated/categories/categories';
const MAP_GROUP_BY_TO_BACKEND: Record<string, string> = {
  user_intent: 'intent',
  topic: 'topic',
  user_action: 'action',
  category: 'sub_topic',
  type_of_problem_v2: 'model_suggestion'
};
export function RenameCategoryDrawer({
  kind,
  oldName,
  children,
  onRename
}: PropsWithChildren<{
  kind: string;
  oldName: string;
  onRename?: () => void;
}>) {
  const {
    spawnToast
  } = useJobToast();
  const {
    projectId
  } = useProjectsContext();
  const queryClient = useQueryClient();
  const [name, setName] = useState<string>(oldName);
  const [openModal, setOpenModal] = useState(false);
  const {
    data: data_0
  } = useQuery({
    queryKey: [QUERY_KEY_GET_CATEGORY, projectId, kind, oldName],
    queryFn: async ({
      signal
    }) => {
      try {
        const data = await getCategory({
          project_id: projectId!,
          kind: MAP_GROUP_BY_TO_BACKEND[kind],
          name: oldName
        }, {
          signal
        });
        return data;
      } catch (error) {
        if (isHttpError(error) && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!projectId && openModal,
    keepPreviousData: true
  });
  useEffect(() => {
    if (!data_0) return;
    setName(data_0.value ?? '');
    setDescription(humanizeString(data_0.description ?? ''));
  }, [data_0]);
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState<string>(humanizeString(data_0?.description ?? ''));
  const {
    mutateAsync: updateCategory,
    isLoading
  } = useUpdateCategory({
    mutation: {
      onSuccess: job_id => {
        spawnToast(job_id, `Renaming category to "${name}"`, () => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_INTERACTION_DETAILS]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_USER_INTENTS_PAGE__TOTALS]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_USER_USER_INTELLIGENCE_VERTICAL_TABLE__ACTIONS]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_USER_USER_INTELLIGENCE_VERTICAL_TABLE__TOPICS]
          });
          onRename?.();
        });
        setOpenModal(false);
      }
    }
  });

  // ---- validation ----
  const nameError = name.trim().length === 0;
  const isValid = !nameError;
  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // mark as touched to reveal errors on first submit attempt
    setNameTouched(true);
    if (!isValid) return;
    await updateCategory({
      data: {
        project_id: projectId!,
        kind: MAP_GROUP_BY_TO_BACKEND[kind],
        name: oldName,
        new_name: name.trim(),
        new_description: description.trim() === '' ? undefined : description.trim()
      }
    });
  }, [isValid, description, name, projectId, kind, oldName, updateCategory]);
  if (!openModal) return <Slot onClick={() => setOpenModal(true)}>{children}</Slot>;
  return <Sheet open={openModal && !isLoading} onOpenChange={setOpenModal}>
      <SheetTrigger asChild>{children}</SheetTrigger>

      <SheetContent side="right" className="p-0">
        <ResizableContainer handlePosition="start" direction="x" minWidth={400} initialWidth={600} initialHeight={'full'} disableResize={false} className="flex h-full flex-col">
          <SheetHeader className="mb-0 flex flex-row items-center justify-between space-y-0 border-b px-6 py-4">
            <DoubleArrowRightIcon className="h-4 w-4 text-muted-foreground hover:cursor-pointer hover:text-accent-foreground" onClick={() => setOpenModal(false)} />
          </SheetHeader>
          <div className="border-b px-6 py-3">
            <div className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-[#8C9B60]" />
              <SheetTitle className="text-base">Rename category</SheetTitle>
            </div>
          </div>
          <WithLoadingContainer loading={isLoading} hideChildren className="flex h-full grow flex-col">
            <form onSubmit={handleSubmit} noValidate className="flex h-full flex-col">
              <div className="my-2 flex flex-col gap-0 px-6 pt-3">
                <label htmlFor="rename-modal--name" className="mb-6 text-[14px] font-medium">
                  <p className="mb-2 text-xs text-muted-foreground">Name*</p>
                  <Input id="rename-modal--name" value={name} onChange={e_0 => setName(e_0.currentTarget.value)} onBlur={() => setNameTouched(true)} aria-invalid={nameTouched && nameError} disabled={isLoading} className={nameTouched && nameError ? 'border-destructive focus-visible:ring-destructive' : ''} placeholder={humanizeString(data_0?.value ?? '')} />
                  {nameTouched && nameError && <p className="mt-1 text-xs text-destructive">Please enter a name.</p>}
                </label>

                {data_0?.description && data_0?.description !== 'None' && <label htmlFor="rename-modal--description" className="mb-6 text-[14px] font-medium">
                    <p className="mb-2 text-xs text-muted-foreground">Description</p>
                    <Textarea disabled={isLoading} className="h-30" id="rename-modal--description" value={description} onChange={e_1 => setDescription(e_1.currentTarget.value)} placeholder={humanizeString(data_0?.description ?? '')} />
                  </label>}
              </div>

              <SheetFooter className="mt-auto flex items-center justify-end gap-2 px-6 py-2">
                <Button variant="defaultDark" type="button" onClick={() => setOpenModal(false)} disabled={isLoading} className="rounded-xl!">
                  Cancel
                </Button>
                <Button type="submit" variant="default" disabled={!isValid || isLoading} className="rounded-xl!">
                  {isLoading && <Loader2Icon className="mr-2 size-4 animate-spin" />}
                  Confirm
                </Button>
              </SheetFooter>
              <div className="h-12.25 border-t"></div>
            </form>
          </WithLoadingContainer>
        </ResizableContainer>
      </SheetContent>
    </Sheet>;
}